import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  id: "overview-list-detail",
  class: "body"
}
const _hoisted_2 = { class: "body__header" }
const _hoisted_3 = { class: "body__header__desc" }
const _hoisted_4 = { class: "body__header__title" }
const _hoisted_5 = { class: "body__header__title--text" }
const _hoisted_6 = { class: "body__header__desc" }
const _hoisted_7 = { class: "body__header__control" }
const _hoisted_8 = { class: "body__header__control__filter" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { class: "body__table base-table-wrapper" }
const _hoisted_11 = { class: "body__table__header__inner" }

import Mdi from '@/corePagesModule/components/Mdi.vue';
import { i18n } from '@/i18n';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { DETAIL_HEADER_OPTION, PAGE_CONFIG, PageType } from '../../constants';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { useInventoryDashboardStore } from '../useInventoryDashboardStore';
import InventoryDashboardChildLayout from '../InventoryDashboardChildLayout.vue';
import { DetailAggregateBusinessData, DetailBaseBusinessData } from '../useInventoryDashboardStore/types';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { InventoryDashboardViewModel } from '../inventoryDashboardViewModel';
import { LoadingStatus } from '@/corePagesModule/components/ezTable/types';
import ArrowIcon from '@/corePagesModule/BaseTableContent/components/ArrowIcon.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';
import { HeaderList, isTableType } from '@/coreDomainsModule/table';


export default /*@__PURE__*/_defineComponent({
  __name: 'OverviewListDetailPage',
  setup(__props) {

const t = i18n.t;
const route = useRoute();

const store = useInventoryDashboardStore();
const { getDetailAggregateBusinessData, getDetailBaseBusinessData } = store;
const vm = new InventoryDashboardViewModel();

const loadingStatus = ref<LoadingStatus>({ type: 'loading' });
const sortType = ref<'ASC' | 'DESC'>('DESC');
const sortTarget = ref<string | undefined>(undefined);
const pagingOptions = ref<IPagingOptions>(initPagingOptions(30));
const filterItems = ref<FilterContentItem[]>([]);
const isExcludeNoAccuracy = ref(false);
const DetailAggregateBusinessData = ref<DetailAggregateBusinessData[]>([]);
const DetailBaseBusinessData = ref<DetailBaseBusinessData[]>();
const checkedBusinessId = ref<string[]>([]);

const pageType = computed((): PageType => {
  const curRoute = route.path.split('/').pop();
  if (!curRoute) throw new Error();
  switch (curRoute) {
    case 'stock':
      return PageType.Stock;
    case 'forecast-accuracy':
      return PageType.ForecastAccuracy;
  }
  throw new Error();
});

const pageConfig = computed(() => {
  if (!pageType.value) return { title: 'エラー', desc: '' };
  return PAGE_CONFIG[pageType.value] ?? { title: 'エラー', desc: '' };
});

const headerOption = computed(() => DETAIL_HEADER_OPTION[pageType.value]);
const fullHeaderKeys = computed(() => headerOption.value.map((h) => h.key));
const tableType = computed(() => {
  const rawTableType = 'inventoryDashboard' + pageType.value.charAt(0).toUpperCase() + pageType.value.slice(1);

  if (!isTableType(rawTableType)) {
    console.error(`Invalid table type: ${rawTableType}`);
    throw new Error(`Invalid table type: ${rawTableType}`);
  }
  return rawTableType;
});

const { headerList, updateDisplayHeader } = useTableHeader(fullHeaderKeys, tableType, []);

const modifiableHeaders = computed(
  (): HeaderList =>
    headerOption.value
      .filter((h) => !['checkbox', 'detail', 'headerModify'].includes(h.key))
      .map((h) => ({
        key: h.key,
        text: vm.convertHeaderKeyToDisplayValue(h.key),
        isShown: headerList.value.includes(h.key),
      }))
);
const gridTemplateColumns = computed(() =>
  headerOption.value.filter((header) => headerList.value.includes(header.key)).map((h) => `minmax(${h.width}px, ${h.width}fr)`)
);
/**
 * データフェッチ
 */
const fetch = async () => {
  if (pageType.value === PageType.Stock) {
    await _fetchAggregateBusinessData();
  } else if (pageType.value === PageType.ForecastAccuracy) {
    await _fetchBaseBusinessData();
  }
};

/**
 * 集計データフェッチ
 */
const _fetchAggregateBusinessData = async () => {
  loadingStatus.value.type = 'loading';
  const res = await getDetailAggregateBusinessData(
    sortType.value,
    sortTarget.value,
    pagingOptions.value.pageSize,
    pagingOptions.value.offset,
    filterItems.value
  );
  if (res === null) {
    loadingStatus.value.type = 'error';
    return;
  }
  DetailAggregateBusinessData.value = res[0];
  pagingOptions.value = res[1];
  loadingStatus.value.type = res[0].length === 0 ? 'zero-match' : 'success';
};

/**
 * 関連取引データフェッチ
 */
const _fetchBaseBusinessData = async () => {
  loadingStatus.value.type = 'loading';
  const res = await getDetailBaseBusinessData(
    sortType.value,
    sortTarget.value,
    pagingOptions.value.pageSize,
    pagingOptions.value.offset,
    isExcludeNoAccuracy.value,
    filterItems.value
  );
  if (res === null) {
    loadingStatus.value.type = 'error';
    return;
  }
  DetailBaseBusinessData.value = res[0];
  pagingOptions.value = res[1];
  loadingStatus.value.type = res[0].length === 0 ? 'zero-match' : 'success';
};

const isBaseBusiness = computed(() => pageType.value === PageType.ForecastAccuracy);

/**
 * ヘッダーキーをソート用のキーに変換する
 * @param headerKey - ヘッダーキー
 */
const convertHeaderKeyToSortKey = (headerKey: string) => {
  switch (headerKey) {
    case 'latestInventoryAmount':
      return 'currentInventory';
    case 'lastMonthEndInventoryAmount':
      return 'endInventory';
    case 'lastMonthSalesResultAmount':
      return 'salesResult';
    case 'lastMonthCustomerForecastAmount':
      return 'customerForecast';
    default:
      return headerKey;
  }
};
/**
 * ソートの矢印を押した時のハンドラ
 * @param headerKey - ヘッダーキー
 */
const onClickSortArrowHandler = async (headerKey: string) => {
  const sortKey = convertHeaderKeyToSortKey(headerKey);
  if (sortTarget.value === sortKey) {
    sortType.value = sortType.value === 'ASC' ? 'DESC' : 'ASC';
  } else {
    sortTarget.value = sortKey;
    sortType.value = 'ASC';
  }
  pagingOptions.value = initPagingOptions(pagingOptions.value.pageSize);
  await fetch();
};

/**
 * ページング変更時のハンドラ
 * @param option - pagingOption
 */
const onChangePagingHandler = async (option: IPagingOptions) => {
  console.log(option);
  pagingOptions.value = { ...option };
  await fetch();
};

/**
 * フィルター適用時のハンドラ
 * @param changedFilterItems - 変更したフィルタ情報
 */
const onApplyFilterHandler = async (changedFilterItems: FilterContentItem[]) => {
  filterItems.value = [...changedFilterItems];
  await fetch();
};

/**
 * 内示正答率がない取引を除外するかどうかのチェックボックス操作時のハンドラ
 * @param isChecked - チェックされたかどうか
 */
const onApplyExcludeNoAccuracy = async (isChecked: boolean) => {
  isExcludeNoAccuracy.value = isChecked;
  await fetch();
};

/**
 * チェックされたかどうか
 * @param businessId - 対象の取引ID
 * @param isChecked - チェック済みかどうか
 */
const onCheckedRow = (businessId: string, isChecked: boolean) => {
  if (isChecked && !checkedBusinessId.value.includes(businessId)) {
    checkedBusinessId.value.push(businessId);
  } else if (!isChecked && checkedBusinessId.value.includes(businessId)) {
    checkedBusinessId.value = checkedBusinessId.value.filter((b) => b !== businessId);
  }
};

/**
 * ヘッダーを切り替える処理
 * @param headerKey - ヘッダーキー
 * @param isHide - 隠すかどうか
 */
const onToggleHeader = async (headerKey: string, isHide: boolean) => {
  await updateDisplayHeader(headerKey, isHide);
};

onMounted(async () => {
  await fetch();
});

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!

  return (_openBlock(), _createBlock(InventoryDashboardChildLayout, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              (pageConfig.value.icon)
                ? (_openBlock(), _createBlock(Mdi, {
                    key: 0,
                    icon: pageConfig.value.icon.name,
                    variant: pageConfig.value.icon.variant,
                    style: _normalizeStyle(pageConfig.value.icon.style)
                  }, null, 8, ["icon", "variant", "style"]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(t)(pageConfig.value.title)), 1)
            ]),
            _createElementVNode("div", _hoisted_6, _toDisplayString(pageConfig.value.desc && _unref(t)(pageConfig.value.desc)), 1)
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createVNode(FilterModalButton, {
                "filter-modal-type": isBaseBusiness.value ? 'inventoryDashboardDetailBase' : 'inventoryDashboardDetailAgg',
                "select-input-index": 10,
                "filter-items": filterItems.value,
                onApply: onApplyFilterHandler
              }, null, 8, ["filter-modal-type", "filter-items"]),
              (pageType.value === 'forecastAccuracy')
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    _createVNode(RowCheckBox, {
                      "label-text": _unref(t)('内示正答率がない取引を非表示にする'),
                      class: "body__header__control__filter--checkbox",
                      onChangeStatus: onApplyExcludeNoAccuracy
                    }, null, 8, ["label-text"])
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createVNode(Pagination, {
              class: "inventory-header__pagination",
              "paging-options": pagingOptions.value ?? _unref(initPagingOptions)(0),
              "is-loading": loadingStatus.value.type === 'loading',
              "no-bottom-margin": "",
              onOnChangePage: _cache[0] || (_cache[0] = ($event: any) => (onChangePagingHandler($event)))
            }, null, 8, ["paging-options", "is-loading"])
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createVNode(EzTable, {
            "grid-template-columns": gridTemplateColumns.value,
            "loading-status": loadingStatus.value,
            "sticky-header": "",
            "is-vertical-scrollable": ""
          }, {
            header: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(headerList).slice(0, -1), (header, index) => {
                return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                  (header === 'checkbox')
                    ? (_openBlock(), _createBlock(EzHeader, {
                        key: 0,
                        class: "body__table__header"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(RowCheckBox)
                        ], undefined, true),
                        _: 1
                      }))
                    : (header === 'detail')
                      ? (_openBlock(), _createBlock(EzHeader, {
                          key: 1,
                          class: "body__table__header"
                        }))
                      : (_openBlock(), _createBlock(EzHeader, {
                          key: 2,
                          "content-padding": header === 'detail' ? 0 : undefined,
                          "force-center": header === 'detail',
                          class: "body__table__header"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_11, [
                              _createTextVNode(_toDisplayString(_unref(vm).convertHeaderKeyToDisplayValue(header)) + " ", 1),
                              _createVNode(ArrowIcon, {
                                asc: convertHeaderKeyToSortKey(header) === sortTarget.value ? sortType.value === 'ASC' : false,
                                onOnClick: () => onClickSortArrowHandler(header)
                              }, null, 8, ["asc", "onOnClick"])
                            ])
                          ], undefined, true),
                          _: 2
                        }, 1032, ["content-padding", "force-center"]))
                ], 64))
              }), 128)),
              _createVNode(EzHeader, { sticky: "right" }, {
                default: _withCtx(() => [
                  _createVNode(TableHeaderModifyButton, {
                    "modifiable-header-list": modifiableHeaders.value,
                    onToggle: onToggleHeader
                  }, null, 8, ["modifiable-header-list"])
                ], undefined, true),
                _: 1
              })
            ]),
            body: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pageType.value === _unref(PageType).ForecastAccuracy ? DetailBaseBusinessData.value : DetailAggregateBusinessData.value, (data, index1) => {
                return (_openBlock(), _createBlock(EzTableRow, {
                  key: index1,
                  "grid-template-columns": gridTemplateColumns.value
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(headerList).slice(0, -1), (header, index2) => {
                      return (_openBlock(), _createElementBlock(_Fragment, { key: index2 }, [
                        (header === 'checkbox')
                          ? (_openBlock(), _createBlock(EzCell, {
                              key: 0,
                              "is-highlighted": checkedBusinessId.value.includes(data.businessId),
                              class: "body__table__cell"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(RowCheckBox, {
                                  onChangeStatus: (isChecked) => onCheckedRow(data.businessId, isChecked)
                                }, null, 8, ["onChangeStatus"])
                              ], undefined, true),
                              _: 2
                            }, 1032, ["is-highlighted"]))
                          : (header === 'detail')
                            ? (_openBlock(), _createBlock(EzCell, {
                                key: 1,
                                "content-padding": 0,
                                "is-highlighted": checkedBusinessId.value.includes(data.businessId),
                                class: "body__table__cell"
                              }, {
                                default: _withCtx(() => [
                                  (data.businessId)
                                    ? (_openBlock(), _createBlock(_component_RouterLink, {
                                        key: 0,
                                        to: 
                      isBaseBusiness.value
                        ? { name: 'customer-forecast-timeline', params: { defaultBusinessId: data.businessId } }
                        : { name: 'recommend-order', params: { id: data.businessId } }
                    ,
                                        target: "_blank"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(TextButton, {
                                            class: "table-detail-button",
                                            "button-style": "btn-secondary",
                                            "on-click": () => {}
                                          }, {
                                            default: _withCtx(() => [
                                              _createTextVNode(_toDisplayString(_unref(t)('詳細')), 1)
                                            ], undefined, true),
                                            _: 1
                                          })
                                        ], undefined, true),
                                        _: 2
                                      }, 1032, ["to"]))
                                    : _createCommentVNode("", true)
                                ], undefined, true),
                                _: 2
                              }, 1032, ["is-highlighted"]))
                            : (
                    ['aggregateBusinessResponsibleUsers', 'baseBusinessResponsibleUsers', 'forecastForReceiptResponsibleUsers'].includes(
                      header
                    )
                  )
                              ? (_openBlock(), _createBlock(EzCell, {
                                  key: 2,
                                  "is-highlighted": checkedBusinessId.value.includes(data.businessId),
                                  class: "body__table__cell"
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_unref(vm).convertUserAccountToDisplayValue(data[header])), 1)
                                  ], undefined, true),
                                  _: 2
                                }, 1032, ["is-highlighted"]))
                              : (_unref(vm).numberHeaderKeys.includes(header))
                                ? (_openBlock(), _createBlock(EzCell, {
                                    key: 3,
                                    "is-number": "",
                                    "is-highlighted": checkedBusinessId.value.includes(data.businessId),
                                    class: "body__table__cell"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(_unref(vm).convertNumberStringToDisplayValue(header, data[header])), 1)
                                    ], undefined, true),
                                    _: 2
                                  }, 1032, ["is-highlighted"]))
                                : (_openBlock(), _createBlock(EzCell, {
                                    key: 4,
                                    "is-highlighted": checkedBusinessId.value.includes(data.businessId),
                                    class: "body__table__cell"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(data[header] ?? '--'), 1)
                                    ], undefined, true),
                                    _: 2
                                  }, 1032, ["is-highlighted"]))
                      ], 64))
                    }), 128)),
                    _createVNode(EzCell)
                  ], undefined, true),
                  _: 2
                }, 1032, ["grid-template-columns"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["grid-template-columns", "loading-status"])
        ])
      ])
    ], undefined, true),
    _: 1
  }))
}
}

})